import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useUsers } from "../../Query/useUsers";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ScheduleIcon from "@mui/icons-material/Schedule";
import color from "../../Constants/colors";
import Loading from "../Common/Loading";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { ComoButton } from "../Common/ComoButton";
import { ComoInput } from "../Common/ComoInput";
import { ComoSelect } from "../Common/ComoSelect";
import {
  useAvailableCronableTasks,
  useCreateScheduledTask,
  useUpdateScheduledTask,
} from "../../Query/useScheduledTask";
import { getSingleScheduledTask } from "../../Api/scheduledTask";
import {
  CronableParam,
  CronableTask,
  ScheduleType,
  ScheduledTaskPayload,
} from "../../Models/scheduledTask";
import { ComoSwitch } from "../Common/ComoSwitch";
import { useTranslation } from "react-i18next";

const ScheduledTaskForm = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const isEdit = !!taskId;
  const navigate = useNavigate();
  const { colors: workspace } = useWorkspaceColors();
  const { t } = useTranslation("scheduledTasks");

  const SCHEDULE_TYPE_OPTIONS = [
    { id: ScheduleType.ONCE, label: t("once") },
    { id: ScheduleType.INTERVAL, label: t("interval") },
    { id: ScheduleType.CRON, label: t("cron") },
  ];

  const { data: availableTasks, isLoading: tasksLoading } =
    useAvailableCronableTasks();
  const createMutation = useCreateScheduledTask();
  const updateMutation = useUpdateScheduledTask();

  const [name, setName] = useState("");
  const [taskKey, setTaskKey] = useState("");
  const [userId, setUserId] = useState<number | null>(null);
  const [selectedUserOption, setSelectedUserOption] = useState<{
    id: number;
    label: string;
  } | null>(null);
  const [userSearch, setUserSearch] = useState("");
  const [kwargs, setKwargs] = useState<Record<string, string>>({});
  const [scheduleType, setScheduleType] = useState<number>(ScheduleType.CRON);
  const [runAt, setRunAt] = useState("");
  const [intervalSeconds, setIntervalSeconds] = useState("");
  const [cronExpression, setCronExpression] = useState("0 10 * * *");
  const [enabled, setEnabled] = useState(true);
  const [loadingExisting, setLoadingExisting] = useState(false);

  const selectedTask: CronableTask | undefined = useMemo(
    () => (availableTasks ?? []).find((t) => t.key === taskKey),
    [availableTasks, taskKey],
  );
  const userRequired = !!selectedTask?.inject_user_id;

  const { data: usersResp } = useUsers(1, 20, userSearch);
  const userOptions = useMemo(() => {
    const opts = (usersResp?.users ?? []).map((u) => ({
      id: u.id,
      label: `${u.name} ${u.surname} (${u.email})`,
    }));
    if (
      selectedUserOption &&
      !opts.some((o) => o.id === selectedUserOption.id)
    ) {
      opts.unshift(selectedUserOption);
    }
    return opts;
  }, [usersResp, selectedUserOption]);

  useEffect(() => {
    if (isEdit && taskId) {
      setLoadingExisting(true);
      getSingleScheduledTask(Number(taskId))
        .then((task) => {
          setName(task.name);
          setTaskKey(task.task_key);
          setUserId(task.user_id);
          if (task.user_id) {
            setSelectedUserOption({
              id: task.user_id,
              label:
                task.user_label || task.user_email || `User #${task.user_id}`,
            });
          }
          const stringKwargs: Record<string, string> = {};
          Object.entries(task.kwargs || {}).forEach(([k, v]) => {
            stringKwargs[k] =
              typeof v === "object" ? JSON.stringify(v) : String(v ?? "");
          });
          setKwargs(stringKwargs);
          setScheduleType(task.schedule_type);
          setRunAt(
            task.run_at ? task.run_at.replace(" ", "T").slice(0, 16) : "",
          );
          setIntervalSeconds(
            task.interval_seconds != null ? String(task.interval_seconds) : "",
          );
          setCronExpression(task.cron_expression ?? "");
          setEnabled(task.enabled);
        })
        .finally(() => setLoadingExisting(false));
    }
  }, [isEdit, taskId]);

  const setKwarg = (paramName: string, value: string) => {
    setKwargs((prev) => ({ ...prev, [paramName]: value }));
  };

  const castParam = (param: CronableParam, value: string): unknown => {
    if (value === "" || value == null) return undefined;
    switch (param.type) {
      case "integer":
        return parseInt(value, 10);
      case "number":
        return parseFloat(value);
      case "boolean":
        return value === "true";
      case "json":
        try {
          return JSON.parse(value);
        } catch {
          return value;
        }
      default:
        return value;
    }
  };

  const buildKwargs = (): Record<string, unknown> => {
    const result: Record<string, unknown> = {};
    (selectedTask?.params ?? []).forEach((param) => {
      const casted = castParam(param, kwargs[param.name] ?? "");
      if (casted !== undefined) {
        result[param.name] = casted;
      }
    });
    return result;
  };

  const handleSubmit = () => {
    const payload: ScheduledTaskPayload = {
      name,
      task_key: taskKey,
      user_id: userId,
      kwargs: buildKwargs(),
      schedule_type: scheduleType,
      run_at: scheduleType === ScheduleType.ONCE ? runAt || null : null,
      interval_seconds:
        scheduleType === ScheduleType.INTERVAL && intervalSeconds
          ? parseInt(intervalSeconds, 10)
          : null,
      cron_expression:
        scheduleType === ScheduleType.CRON ? cronExpression || null : null,
      enabled,
    };

    if (isEdit) {
      updateMutation.mutate(
        { id: Number(taskId), data: payload },
        { onSuccess: () => navigate("/scheduled_tasks") },
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: () => navigate("/scheduled_tasks"),
      });
    }
  };

  const scheduleValid =
    (scheduleType === ScheduleType.ONCE && !!runAt) ||
    (scheduleType === ScheduleType.INTERVAL && !!intervalSeconds) ||
    (scheduleType === ScheduleType.CRON && !!cronExpression.trim());

  const requiredKwargsPresent = (selectedTask?.params ?? [])
    .filter((p) => p.required)
    .every((p) => (kwargs[p.name] ?? "").toString().trim() !== "");

  const canSubmit =
    !!name.trim() &&
    !!taskKey &&
    (!userRequired || !!userId) &&
    scheduleValid &&
    requiredKwargsPresent &&
    !createMutation.isPending &&
    !updateMutation.isPending;

  if (tasksLoading || loadingExisting) {
    return (
      <Loading
        backgroundColor={color.gray100}
        color1={workspace.accent}
        color2={workspace.primary_dark}
      />
    );
  }

  const renderParamInput = (param: CronableParam) => {
    if (param.type === "boolean") {
      return (
        <Box key={param.name}>
          <Typography
            variant="body2"
            sx={{ mb: 1, fontWeight: 500, color: color.primary_dark }}
          >
            {param.label}
            {param.required ? " *" : ""}
          </Typography>
          <ComoSelect
            options={[
              { id: "true", label: t("true") },
              { id: "false", label: t("false") },
            ]}
            selectedOption={
              kwargs[param.name]
                ? {
                    id: kwargs[param.name],
                    label: kwargs[param.name] === "true" ? "True" : "False",
                  }
                : undefined
            }
            onSelect={(option) => setKwarg(param.name, String(option.id))}
            placeholder="Select"
          />
          {param.description && (
            <Typography variant="caption" sx={{ color: color.gray500 }}>
              {param.description}
            </Typography>
          )}
        </Box>
      );
    }

    const isNumeric = param.type === "integer" || param.type === "number";
    const isLongText = param.name === "prompt";
    return (
      <Box key={param.name}>
        <ComoInput
          label={`${param.label}${param.required ? " *" : ""}`}
          value={kwargs[param.name] ?? ""}
          onChange={(e) => setKwarg(param.name, e.target.value)}
          type={isNumeric ? "number" : "text"}
          multiline={isLongText}
          minRows={isLongText ? 3 : undefined}
          fullWidth
        />
        {param.description && (
          <Typography variant="caption" sx={{ color: color.gray500 }}>
            {param.description}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{ minHeight: "calc(100vh - 64px)", backgroundColor: color.gray50 }}
    >
      <Box sx={{ maxWidth: "1024px", mx: "auto", px: { xs: 2, sm: 3 }, py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${color.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: color.white,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <IconButton
              onClick={() => navigate("/scheduled_tasks")}
              size="small"
              sx={{ color: workspace.primary_dark }}
            >
              <ArrowBackIcon />
            </IconButton>
            <ScheduleIcon
              sx={{ fontSize: 32, color: workspace.primary_dark }}
            />
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, color: workspace.primary_dark }}
            >
              {isEdit ? t("editTitle") : t("createTitle")}
            </Typography>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: `1px solid ${color.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: color.white,
            overflow: "visible",
          }}
        >
          <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
            <ComoInput
              label={t("name")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              fullWidth
            />

            <Box>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontWeight: 500, color: color.primary_dark }}
              >
                {userRequired ? t("userRequired") : t("userOptional")}
              </Typography>
              <Autocomplete
                options={userOptions}
                value={selectedUserOption}
                onChange={(_, value) => {
                  setSelectedUserOption(value);
                  setUserId(value ? value.id : null);
                }}
                onInputChange={(_, value) => setUserSearch(value)}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(opts) => opts}
                slotProps={{
                  paper: {
                    sx: {
                      "& .MuiAutocomplete-option": {
                        color: color.primary_dark,
                      },
                    },
                  },
                }}
                renderInput={(params) => (
                  <ComoInput
                    {...params}
                    placeholder={t("userSearchPlaceholder")}
                    sx={{
                      "& .MuiInputBase-input": {
                        color: color.primary_dark,
                        WebkitTextFillColor: color.primary_dark,
                      },
                    }}
                  />
                )}
              />
            </Box>

            <Box>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontWeight: 500, color: color.primary_dark }}
              >
                {t("task")}
              </Typography>
              {isEdit ? (
                <Box
                  sx={{
                    py: 1.25,
                    px: 2,
                    bgcolor: color.gray50,
                    border: `1px solid ${color.gray300}`,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: color.primary_dark }}
                  >
                    {selectedTask?.name ?? taskKey}
                  </Typography>
                </Box>
              ) : (
                <ComoSelect
                  options={(availableTasks ?? []).map((t) => ({
                    id: t.key,
                    label: t.name,
                  }))}
                  selectedOption={
                    selectedTask
                      ? { id: selectedTask.key, label: selectedTask.name }
                      : undefined
                  }
                  onSelect={(option) => setTaskKey(String(option.id))}
                  placeholder={t("selectTaskPlaceholder")}
                />
              )}
              {selectedTask?.description && (
                <Typography variant="caption" sx={{ color: color.gray500 }}>
                  {selectedTask.description}
                </Typography>
              )}
            </Box>

            {selectedTask && selectedTask.params.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.5,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: color.gray50,
                  border: `1px solid ${color.gray200}`,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: color.primary_dark }}
                >
                  {t("taskArguments")}
                </Typography>
                {selectedTask.params.map(renderParamInput)}
              </Box>
            )}

            <Box>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontWeight: 500, color: color.primary_dark }}
              >
                {t("schedule")}
              </Typography>
              <ComoSelect
                options={SCHEDULE_TYPE_OPTIONS.map((o) => ({
                  id: o.id,
                  label: o.label,
                }))}
                selectedOption={SCHEDULE_TYPE_OPTIONS.map((o) => ({
                  id: o.id,
                  label: o.label,
                })).find((o) => o.id === scheduleType)}
                onSelect={(option) => setScheduleType(Number(option.id))}
                placeholder={t("selectScheduleType")}
              />
            </Box>

            {scheduleType === ScheduleType.ONCE && (
              <ComoInput
                label={t("runAt")}
                value={runAt}
                onChange={(e) => setRunAt(e.target.value)}
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            )}
            {scheduleType === ScheduleType.INTERVAL && (
              <ComoInput
                label={t("intervalSeconds")}
                value={intervalSeconds}
                onChange={(e) => setIntervalSeconds(e.target.value)}
                type="number"
                fullWidth
              />
            )}
            {scheduleType === ScheduleType.CRON && (
              <Box>
                <ComoInput
                  label={t("cronExpression")}
                  value={cronExpression}
                  onChange={(e) => setCronExpression(e.target.value)}
                  fullWidth
                />
                <Typography variant="caption" sx={{ color: color.gray500 }}>
                  {t("cronExample")}
                </Typography>
              </Box>
            )}

            <FormControlLabel
              control={
                <ComoSwitch
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: color.primary_dark }}>
                  {t("enabled")}
                </Typography>
              }
            />
          </Box>

          <Box
            sx={{
              p: 3,
              display: "flex",
              gap: 2,
              justifyContent: "flex-end",
              borderTop: `1px solid ${color.gray200}`,
            }}
          >
            <ComoButton
              variant="outlined"
              onClick={() => navigate("/scheduled_tasks")}
            >
              {t("cancel")}
            </ComoButton>
            <ComoButton onClick={handleSubmit} disabled={!canSubmit}>
              {isEdit ? t("update") : t("create")}
            </ComoButton>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default ScheduledTaskForm;
